import { use, useCallback, useEffect, useRef, useState } from 'react'
import {
  authenticateWebAuthn,
  isWebAuthnSupported,
} from '../../api/authenticateWebAuthn'
import Link from '@talkdesk/cobalt-react-link'
import Flex from '@talkdesk/cobalt-react-flex'
import { Text } from '@talkdesk/cobalt-react-typography'
import MFAHeader from '../MFAHeader'
import ErrorMessage from '../ErrorMessage'
import WebAuthnLoginAnimation from './WebAuthnLoginAnimation'
import { WEB_AUTH as WebAuthnType } from '../../constants/mfa.types'
import useMFA from '../../hooks/useMFA'
import { LoginContext } from '../../contexts/Login'
import { useTranslation } from 'react-i18next'
import { LEARN_MORE_ABOUT_SECURITY_KEYS } from '../../constants/links'
import styles from './WebAuthn.module.css'

export const WebAuthn = ({
  talkdeskIdAssetsURL,
  csrf,
  ctx,
  data,
  webAuthnTokenTtl,
  loginTimestamp,
  webAuthnTokenTtlHeadroomSeconds = 5,
  brandingSettings,
}) => {
  const { t } = useTranslation()
  const mountedRef = useRef(true)
  const [showSuccess, setShowSuccess] = useState(false)
  const [showError, setShowError] = useState(false)
  const [errorType, setErrorType] = useState(null)

  const {
    doLogin,
    errorMessage,
    showError: showMfaError,
  } = useMFA({
    invalidCodeErrorMessage: () => t('webauthn.invalidError'),
    genericErrorMessage: () => t('webauthn.invalidError'),
  })

  const handleSignSuccess = useCallback(
    (result) => {
      setShowError(false)
      setErrorType(null)
      setShowSuccess(true)
      doLogin(csrf, ctx.credentials, {
        type: WebAuthnType,
        payload: { requestId: data.requestId, credential: result },
      })
    },
    [doLogin, csrf, ctx, data],
  )

  const sign = useCallback(() => {
    authenticateWebAuthn({
      ...data.assertionRequest.publicKeyCredentialRequestOptions,
      timeout: Math.max(
        0,
        (loginTimestamp +
          webAuthnTokenTtl -
          webAuthnTokenTtlHeadroomSeconds -
          new Date().getTime() / 1000) *
          1000,
      ),
    }).then(
      (result) => {
        if (mountedRef.current) handleSignSuccess(result)
      },
      () => {
        if (mountedRef.current) {
          setErrorType('sign_error')
          setShowError(true)
        }
      },
    )
  }, [
    data,
    loginTimestamp,
    webAuthnTokenTtl,
    webAuthnTokenTtlHeadroomSeconds,
    handleSignSuccess,
  ])

  const ensureSupported = useCallback(
    () =>
      new Promise((resolve, reject) => {
        if (isWebAuthnSupported()) {
          resolve()
        } else {
          reject()
        }
      }).catch(() => {
        setErrorType('not_supported')
        setShowError(true)
        return Promise.reject()
      }),
    [],
  )

  const signIfSupported = useCallback(
    () => ensureSupported().then(sign, () => {}),
    [ensureSupported, sign],
  )

  const retry = () => {
    setShowError(false)
    setErrorType(null)
    setTimeout(signIfSupported, 500)
  }

  useEffect(() => {
    mountedRef.current = true
    signIfSupported()
    return () => {
      mountedRef.current = false
    }
  }, [signIfSupported])

  const renderErrorMessage = () => {
    if (errorType === 'not_supported' || errorType === 'sign_error') {
      return (
        <Text inline>
          {t('webauthn.notSupportedErrorPart1')}{' '}
          <Link onClick={retry}>{t('mfa.retryAction')}</Link>
          {'. ' + t('webauthn.notSupportedErrorPart2') + ' '}
          <Link
            target='_blank'
            href={LEARN_MORE_ABOUT_SECURITY_KEYS(brandingSettings)}
          >
            {t('webauthn.help')}
          </Link>
        </Text>
      )
    }
    return errorMessage || null
  }

  return (
    <>
      <MFAHeader
        icon='security_key'
        iconColor='primary600'
        title={t('webauthn.title')}
        subtitle={t('webauthn.description')}
      />
      {(showError || showMfaError) && (errorType || errorMessage) ? (
        <ErrorMessage>{renderErrorMessage()}</ErrorMessage>
      ) : null}
      <Flex
        alignX='center'
        alignY='center'
        height='146px'
        padding={4}
        className={styles.securityKeyBox}
      >
        <WebAuthnLoginAnimation
          talkdeskIdAssetsURL={talkdeskIdAssetsURL}
          isValid={showSuccess}
        />
      </Flex>
    </>
  )
}

const WebAuthWithContext = (props) => {
  const ctx = use(LoginContext)
  return <WebAuthn ctx={ctx} {...props} />
}
WebAuthWithContext.displayName = 'WEB_AUTHN'

export default WebAuthWithContext
